/**
 * Contact Form Module
 * DSS - DeSight Shopify Theme
 * Handles form validation, floating contact button popup, and map behaviors
 */

import { $, $$ } from '../utils/helpers.js';

console.log('[DSS] Contact Form Module geladen', { timestamp: Date.now() });

class CustomSelect {
  constructor(select) {
    console.log('[DSS] CustomSelect erstellt für:', select.name || select.id, { select });
    this.select = select;
    if (this.select.dataset.dssCustomSelect === 'true') {
      console.log('[DSS] CustomSelect bereits initialisiert, überspringe');
      return;
    }

    this.select.dataset.dssCustomSelect = 'true';
    this.options = Array.from(this.select.options);
    this.isOpen = false;
    this.selectedIndex = this.select.selectedIndex;

    console.log('[DSS] CustomSelect Optionen:', this.options.map(o => o.textContent));
    this.build();
    this.bindEvents();
    console.log('[DSS] CustomSelect fertig initialisiert');
  }

  build() {
    console.log('[DSS] CustomSelect build()');
    this.select.style.display = 'none';

    this.wrapper = document.createElement('div');
    this.wrapper.className = 'dss-custom-select';
    this.wrapper.setAttribute('role', 'combobox');
    this.wrapper.setAttribute('aria-haspopup', 'listbox');
    this.wrapper.setAttribute('aria-expanded', 'false');

    this.trigger = document.createElement('button');
    this.trigger.className = 'dss-custom-select__trigger';
    this.trigger.type = 'button';
    this.trigger.setAttribute('aria-label', this.select.getAttribute('aria-label') || 'Auswählen');
    this.trigger.innerHTML = `
      <span class="dss-custom-select__value">${this.getDisplayText()}</span>
      <span class="dss-custom-select__arrow" aria-hidden="true">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </span>
    `;

    this.updatePlaceholderState();

    this.dropdown = document.createElement('ul');
    this.dropdown.className = 'dss-custom-select__dropdown';
    this.dropdown.setAttribute('role', 'listbox');
    this.dropdown.id = `dss-cs-list-${this.select.name || Math.random().toString(36).slice(2, 7)}`;

    this.trigger.setAttribute('aria-controls', this.dropdown.id);

    this.options.forEach((option, index) => {
      const li = document.createElement('li');
      li.className = 'dss-custom-select__option';
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', index === this.selectedIndex ? 'true' : 'false');
      li.dataset.index = index;
      li.dataset.value = option.value;
      li.textContent = option.textContent;

      if (option.disabled) li.classList.add('dss-custom-select__option--disabled');
      if (option.selected || index === this.selectedIndex) li.classList.add('dss-custom-select__option--selected');

      this.dropdown.appendChild(li);
    });

    this.wrapper.appendChild(this.trigger);
    this.wrapper.appendChild(this.dropdown);
    this.select.parentNode.insertBefore(this.wrapper, this.select.nextSibling);
  }

  getDisplayText() {
    const selected = this.options[this.selectedIndex];
    if (selected && !selected.disabled) return selected.textContent;
    const firstReal = this.options.find(o => !o.disabled);
    return firstReal ? firstReal.textContent : '';
  }

  updatePlaceholderState() {
    const selected = this.options[this.selectedIndex];
    const isPlaceholder = !selected || selected.disabled || selected.value === '';
    this.wrapper.classList.toggle('dss-custom-select--placeholder', isPlaceholder);
  }

  bindEvents() {
    this.trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggle();
    });

    this.trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggle();
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!this.isOpen) this.open();
        else this.navigate(e.key === 'ArrowDown' ? 1 : -1);
      }
    });

    this.dropdown.querySelectorAll('.dss-custom-select__option').forEach((li) => {
      li.addEventListener('click', () => {
        if (li.classList.contains('dss-custom-select__option--disabled')) return;
        this.setValue(parseInt(li.dataset.index));
        this.close();
      });

      li.addEventListener('mousedown', (e) => {
        if (li.classList.contains('dss-custom-select__option--disabled')) e.preventDefault();
      });
    });

    document.addEventListener('click', (e) => {
      if (this.isOpen && !this.wrapper.contains(e.target)) {
        this.close();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    this.select.addEventListener('change', () => {
      this.syncFromNative();
    });
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.isOpen = true;
    this.wrapper.classList.add('active');
    this.wrapper.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.isOpen = false;
    this.wrapper.classList.remove('active');
    this.wrapper.setAttribute('aria-expanded', 'false');
  }

  setValue(index) {
    this.selectedIndex = index;
    this.select.selectedIndex = index;

    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this.select.dispatchEvent(new Event('input', { bubbles: true }));

    this.trigger.querySelector('.dss-custom-select__value').textContent = this.options[index].textContent;
    this.updatePlaceholderState();

    this.dropdown.querySelectorAll('.dss-custom-select__option').forEach((li, i) => {
      li.classList.toggle('dss-custom-select__option--selected', i === index);
      li.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
  }

  navigate(direction) {
    const items = this.dropdown.querySelectorAll('.dss-custom-select__option:not(.dss-custom-select__option--disabled)');
    const currentIdx = Array.from(items).findIndex(li =>
      parseInt(li.dataset.index) === this.selectedIndex
    );
    const nextIdx = currentIdx + direction;

    if (nextIdx >= 0 && nextIdx < items.length) {
      const target = items[nextIdx];
      this.setValue(parseInt(target.dataset.index));
      target.scrollIntoView({ block: 'nearest' });
    }
  }

  syncFromNative() {
    const idx = this.select.selectedIndex;
    if (idx !== this.selectedIndex) {
      this.selectedIndex = idx;
      this.trigger.querySelector('.dss-custom-select__value').textContent = this.options[idx]?.textContent || '';
      this.dropdown.querySelectorAll('.dss-custom-select__option').forEach((li, i) => {
        li.classList.toggle('dss-custom-select__option--selected', i === idx);
        li.setAttribute('aria-selected', i === idx ? 'true' : 'false');
      });
    }
  }

  static initAll(container) {
    const root = container || document;
    const selects = root.querySelectorAll('select.field__input, select.dss-custom-select--init');
    selects.forEach(select => {
      if (!select.closest('.dss-custom-select') && !select.dataset.dssCustomSelect) {
        new CustomSelect(select);
      }
    });
  }
}

class ContactForm {
  constructor(container) {
    console.log('[DSS] ContactForm constructor', { container });
    this.container = container;
    if (!this.container) {
      console.log('[DSS] ContactForm: kein Container, überspringe');
      return;
    }
    this.form = this.container.querySelector('form');
    if (!this.form) {
      console.log('[DSS] ContactForm: kein Formular gefunden in Container');
      return;
    }
    console.log('[DSS] ContactForm: Formular gefunden', { form: this.form, action: this.form.action, id: this.form.id });
    this.init();
  }

  init() {
    console.log('[DSS] ContactForm init()');
    this.bindValidation();
    this.bindSubmit();
  }

  bindValidation() {
    console.log('[DSS] ContactForm: binde Validierung');
    const requiredFields = this.form.querySelectorAll('[required]');
    console.log('[DSS] ContactForm: required fields gefunden:', requiredFields.length);
    requiredFields.forEach(field => {
      field.addEventListener('invalid', (e) => this.handleInvalid(e));
      field.addEventListener('input', (e) => this.clearError(e));
    });
  }

  bindSubmit() {
    console.log('[DSS] ContactForm: binde Submit-Event');
    const submitBtn = this.form.querySelector('button[type="submit"]');
    if (!submitBtn) return;
    submitBtn.addEventListener('click', (e) => {
      console.log('[DSS] ContactForm: Submit-Button geklickt');
      const isValid = this.form.checkValidity();
      if (!isValid) return;
      e.preventDefault();
      const btn = e.currentTarget;
      btn.disabled = true;
      btn.innerHTML = 'Wird gesendet...';
      setTimeout(() => {
        this.form.submit();
      }, 200);
    });
  }

  handleInvalid(e) {
    console.log('[DSS] ContactForm: invalid event für Feld', e.target.name || e.target.id);
    e.preventDefault();
    const field = e.target;
    this.showError(field);
    field.focus();
  }

  showError(field) {
    const wrapper = field.closest('.field');
    if (!wrapper) return;
    wrapper.querySelector('.field__input')?.classList.add('error');
    const existing = wrapper.querySelector('.dss-contact-form__field-error');
    if (!existing) {
      const error = document.createElement('small');
      error.className = 'dss-contact-form__field-error';
      error.textContent = 'Dieses Feld ist erforderlich';
      wrapper.appendChild(error);
    }
  }

  clearError(e) {
    const field = e.target;
    const wrapper = field.closest('.field');
    if (!wrapper) return;
    field.classList.remove('error');
    const error = wrapper.querySelector('.dss-contact-form__field-error');
    if (error) error.remove();
  }
}

class FloatingContactButton {
  constructor() {
    console.log('[DSS] FloatingContactButton constructor');
    this.btn = $('#dss-floating-contact-btn');
    this.popup = $('#dss-floating-contact-popup');
    this.overlay = $('#dss-floating-contact-overlay');
    this.closeBtn = $('#dss-floating-contact-popup-close');

    console.log('[DSS] FloatingContactButton elements:', {
      btn: !!this.btn,
      popup: !!this.popup,
      overlay: !!this.overlay,
      closeBtn: !!this.closeBtn
    });

    if (!this.btn || !this.popup) {
      console.log('[DSS] FloatingContactButton: Button oder Popup nicht gefunden, überspringe');
      return;
    }
    this.init();
  }

  init() {
    console.log('[DSS] FloatingContactButton init()');
    this.bindEvents();

    // Falls nach Formular-Absenden via ?contact_posted=true umgeleitet wurde,
    // Popup automatisch öffnen, damit die Erfolgsmeldung sichtbar ist
    if (window.location.search.includes('contact_posted=true')) {
      this.open();
      const header = this.popup.querySelector('.dss-floating-contact__popup-header');
      const text = this.popup.querySelector('.dss-floating-contact__popup-text');
      if (header) header.style.display = 'none';
      if (text) text.style.display = 'none';
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
    }

    // Auch hier Submit-Listener für das Popup-Formular
    const popupForm = this.popup.querySelector('form');
    if (popupForm) {
      console.log('[DSS] FloatingContactButton: Popup-Formular gefunden', { action: popupForm.action, id: popupForm.id });
      const popupSubmitBtns = popupForm.querySelectorAll('button[type="submit"]');
      console.log('[DSS] FloatingContactButton: Popup Submit-Buttons:', popupSubmitBtns.length);
      popupForm.addEventListener('submit', (e) => {
        console.log('[DSS] **** POPUP FORM SUBMIT ****', {
          action: popupForm.action,
          formData: [...new FormData(popupForm).entries()]
        });
      });
    } else {
      console.log('[DSS] FloatingContactButton: KEIN Formular im Popup gefunden!');
    }
  }

  bindEvents() {
    console.log('[DSS] FloatingContactButton bindEvents()');
    this.btn.addEventListener('click', () => {
      console.log('[DSS] Floating Button geklickt');
      this.open();
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => {
        console.log('[DSS] Popup Close Button geklickt');
        this.close();
      });
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        console.log('[DSS] Popup Overlay geklickt');
        this.close();
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.classList.contains('active')) {
        console.log('[DSS] Escape gedrückt, schließe Popup');
        this.close();
      }
    });

    document.addEventListener('shopify:section:load', () => {
      this.reinit();
    });
  }

  open() {
    console.log('[DSS] FloatingContactButton open()');
    this.popup.classList.add('active');
    this.btn.setAttribute('aria-expanded', 'true');
    if (this.overlay) this.overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    this.popup.querySelector('input')?.focus();

    const header = this.popup.querySelector('.dss-floating-contact__popup-header');
    const text = this.popup.querySelector('.dss-floating-contact__popup-text');
    if (header) header.style.display = '';
    if (text) text.style.display = '';

    const resetBtn = this.popup.querySelector('[data-floating-close]');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => this.close());
    }

    this.addProductField();
  }

  addProductField() {
    const form = this.popup.querySelector('form');
    if (!form) return;

    if (form.querySelector('.dss-product-field')) return;

    const isProductPage = window.location.pathname.includes('/products/');
    if (!isProductPage) return;

    const productTitleEl = document.querySelector('.product__title h1');
    if (!productTitleEl) return;

    const productName = productTitleEl.textContent.trim();
    if (!productName) return;

    let sku = '';
    const variantJsonScript = document.querySelector('[data-selected-variant]');
    if (variantJsonScript) {
      try {
        const variantData = JSON.parse(variantJsonScript.textContent);
        sku = variantData.sku || '';
      } catch (e) {}
    }

    let betreff = `Fragen zu ${productName} stellen`;
    if (sku) {
      betreff += ` (${sku})`;
    }

    const fieldGroup = document.createElement('div');
    fieldGroup.className = 'dss-contact-form__field-group dss-product-field';
    fieldGroup.innerHTML = `
      <div class="dss-contact-form__field field">
        <input
          class="field__input"
          type="text"
          id="FloatingContact-product"
          value="${betreff}"
          disabled
          readonly
          placeholder=" "
        >
        <label class="field__label" for="FloatingContact-product">Betreff</label>
        <input type="hidden" name="contact[Betreff]" value="${betreff}">
      </div>
    `;

    const firstFieldGroup = form.querySelector('.dss-contact-form__field-group');
    if (firstFieldGroup) {
      form.insertBefore(fieldGroup, firstFieldGroup);
    }
  }

  close() {
    console.log('[DSS] FloatingContactButton close()');
    this.popup.classList.remove('active');
    this.btn.setAttribute('aria-expanded', 'false');
    if (this.overlay) this.overlay.classList.remove('active');
    document.body.style.overflow = '';
    this.btn.focus();
  }

  reinit() {
    console.log('[DSS] FloatingContactButton reinit()');
    this.btn = $('#dss-floating-contact-btn');
    this.popup = $('#dss-floating-contact-popup');
    this.overlay = $('#dss-floating-contact-overlay');
    this.closeBtn = $('#dss-floating-contact-popup-close');
    if (this.btn && this.popup) this.bindEvents();
  }
}

function initCustomSelects(container) {
  console.log('[DSS] initCustomSelects()', { container: container ? 'ja' : 'nein (global)' });
  CustomSelect.initAll(container);
}

function initContactForms() {
  console.log('[DSS] initContactForms()');
  const formContainers = $$('.dss-contact-form');
  console.log('[DSS] DSS Kontaktformular-Container gefunden:', formContainers.length);
  formContainers.forEach(container => {
    new ContactForm(container);
    initCustomSelects(container);
  });
}

function initFloatingContact() {
  console.log('[DSS] initFloatingContact()');
  const btnExists = !!$('#dss-floating-contact-btn');
  console.log('[DSS] Floating Button existiert:', btnExists);
  if (btnExists) {
    new FloatingContactButton();
  }
}

console.log('[DSS] Module: readyState =', document.readyState);
if (document.readyState === 'loading') {
  console.log('[DSS] Module: warte auf DOMContentLoaded');
  document.addEventListener('DOMContentLoaded', () => {
    console.log('[DSS] Module: DOMContentLoaded');
    initContactForms();
    initFloatingContact();
  });
} else {
  console.log('[DSS] Module: DOM bereits geladen, starte sofort');
  initContactForms();
  initFloatingContact();
}

  console.log('[DSS] Module: main.js Handler registriert');

document.addEventListener('shopify:section:load', (event) => {
  console.log('[DSS] shopify:section:load', { target: event.target });
  const contactForms = event.target?.querySelectorAll?.('.dss-contact-form');
  if (contactForms) {
    console.log('[DSS] DSS Kontaktformulare in Section:', contactForms.length);
    contactForms.forEach(container => {
      new ContactForm(container);
      initCustomSelects(container);
    });
  }
  if (event.target) {
    initCustomSelects(event.target);
  }
  if ($('#dss-floating-contact-btn', event.target)) {
    console.log('[DSS] Floating Button in Section gefunden');
    new FloatingContactButton();
  }
});

export { CustomSelect, ContactForm, FloatingContactButton, initContactForms, initFloatingContact, initCustomSelects };
